import { Box, Flex } from "@chakra-ui/layout";
import { Text } from "@chakra-ui/react";
import { Col, Row } from "antd";
import useAdicionarContaRateio from "features/rateio/hooks/useAdicionarContaRateio";
import { OperacaoEnum } from "core/resources/enums/conta";
import useFiltro from "infra/helpers/useFiltro";
import moment from "moment";
import { useState, useMemo } from "react";
import OutlineButton from "components/Button/OutlineButton";
import DefaultButton from "components/Button/PrimaryButton";
import useContasPassiveisDeAdicaoAoRateio from "core/features/rateio/hooks/useContaRecorrenteLancamentos";
import { ContaPassivelDeAdicaoAoRateio } from "core/features/rateio/typings";
import useContasRateioColumns from "core/features/rateio/hooks/useContasRateioColumns";
import HeaderTable from "components/Page/HeaderTable";
import NewTable from "components/NewTable";
import InfiniteScroll from "react-infinite-scroll-component";
import InfiniteScrollLoader from "components/InfiniteScrollLoader";

interface FormAdicionarContasProps {
  onSuccess: (data: any) => Promise<void>;
  onClose?: () => void;
  rateioId: string;
  year: string;
  month: string;
}

function FormAdicionarContas({
  onSuccess,
  onClose,
  rateioId,
  year,
  month,
}: FormAdicionarContasProps) {
  const { filtro, onChangeFiltro } = useFiltro();
  const [selectedItems, setSelectedItems] = useState<
    ContaPassivelDeAdicaoAoRateio[]
  >([]);

  const data = moment(`${year}-${month}-01`);

  const {
    isLoading: isLoadingContas,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    data: contasPages,
  } = useContasPassiveisDeAdicaoAoRateio(rateioId, {
    operacao: OperacaoEnum.Despesa,
    dataInicio: data.clone().subtract(1, "month").format("YYYY-MM-DD"),
    dataFim: data.clone().endOf("month").format("YYYY-MM-DD"),
    search: filtro.search,
    limit: 10,
  });

  const { isLoading: isLoadingAdicionarContas, mutate: handleAdicionarContas } =
    useAdicionarContaRateio({
      id: rateioId,
      year,
      month,
      onSuccess: (data) => {
        onSuccess(data);
      },
    });

  const onSubmit = async () => {
    const ids = items.map((i) => i.id);
    handleAdicionarContas({
      contas: ids,
    });
  };

  const isLoadingMutate = isLoadingAdicionarContas;

  const items = useMemo(() => {
    return (
      contasPages?.pages.reduce(
        (agg: ContaPassivelDeAdicaoAoRateio[], page) =>
          agg.concat(page.items ?? []),
        []
      ) ?? []
    );
  }, [contasPages]);

  const columns = useContasRateioColumns();

  return (
    <Flex flexDir="column" flex={1} h="100%" flexGrow={1}>
      <Row>
        <Box as={Col} sm={24} pb={16} pt={4} overflow="hidden">
          <Text as="h3" fontWeight="500" fontSize={24} mt={-6} mb={4}>
            Adicionar contas ao rateio
          </Text>

          <Box
            maxH="40vh"
            className="custom-scroll-bar"
            overflowY="auto"
            id="lista-contas-adicionar-rateio"
          >
            <InfiniteScroll
              scrollableTarget="lista-contas-adicionar-rateio"
              dataLength={items.length}
              next={() => {
                fetchNextPage();
              }}
              scrollThreshold={0.1}
              hasMore={!!hasNextPage}
              loader={isFetchingNextPage ? <InfiniteScrollLoader /> : null}
            >
              <NewTable
                hideShadow
                title={() => (
                  <HeaderTable
                    value={filtro.search ?? ""}
                    onSearch={(q) => onChangeFiltro("search", q)}
                  />
                )}
                loading={isLoadingContas}
                columns={columns}
                dataSource={items ?? []}
                customSelectedRows={{
                  handler: setSelectedItems,
                  selecteds: selectedItems,
                  actions: [],
                }}
              />
            </InfiniteScroll>
          </Box>
        </Box>
      </Row>

      <Box
        pt="32px"
        justifyContent="flex-end"
        d="flex"
        gridGap="8px"
        flexDir="row"
        marginTop="auto"
        pb={"16px"}
      >
        <OutlineButton
          type="button"
          disabled={isLoadingMutate}
          onClick={onClose}
        >
          Cancelar
        </OutlineButton>

        <DefaultButton
          type="button"
          isLoading={isLoadingMutate}
          onClick={onSubmit}
          disabled={isLoadingMutate || !items.length}
          minWidth={"120px"}
        >
          Adicionar
        </DefaultButton>
      </Box>
    </Flex>
  );
}

export default FormAdicionarContas;
